import { UzDateRangePicker, type UzDateRange } from '@utazzo/ui';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useWhatsAppLogCalls, useWhatsAppLogMessages } from '../use-whatsapp-log';

import type { WhatsAppLogCallItem, WhatsAppLogMessageItem } from '@utazzo/shared';

import { DataGrid, type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { PageShell } from '@/components/page-shell';
import { useDataGridState } from '@/hooks/use-data-grid-state';

/**
 * Admin-only WhatsApp delivery log.
 *
 * The chat widget shows agents nothing when a message fails - they cannot fix
 * a closed window, a wrong number or an unapproved template, and a red line
 * under a bubble only makes them doubt the tool. Those failures are operations
 * work, so they surface here: what failed, to whom, on whose lead, and the
 * provider's own wording.
 *
 * Two tabs because messages and calls have different columns; both default to
 * "problems only" and can be switched to everything.
 */

type Tab = 'messages' | 'calls';

/** IST day as YYYY-MM-DD, matching the API's day bounds. */
const isoDay = (d: Date | null): string | undefined =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : undefined;

const formatWhen = (iso: string): string =>
  new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

const SCOPE_FILTER: ReadonlyArray<DataGridFilter> = [
  {
    id: 'scope',
    label: 'Rows',
    options: [
      { value: '', label: 'Problems only' },
      { value: 'all', label: 'Everything' },
    ],
  },
];

const StatusPill = ({ status }: { status: string }): React.JSX.Element => {
  const bad = ['FAILED', 'MISSED', 'REJECT'].includes(status);
  const good = ['DELIVERED', 'READ', 'END', 'SENT'].includes(status);
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
        bad
          ? 'bg-rose-100 text-rose-700'
          : good
            ? 'bg-emerald-100 text-emerald-700'
            : 'bg-zinc-100 text-zinc-600'
      }`}
    >
      {status}
    </span>
  );
};

/** Customer identity is the same in both tabs: name over number. */
const Customer = ({ name, phone }: { name: string | null; phone: string }): React.JSX.Element => (
  <div className="flex flex-col">
    <span className="text-sm text-zinc-900">{name ?? `+${phone}`}</span>
    {name && <span className="text-[11px] text-zinc-500">+{phone}</span>}
  </div>
);

const LeadCell = ({
  leadId,
  reference,
  agentName,
}: {
  leadId: string | null;
  reference: string | null;
  agentName: string | null;
}): React.JSX.Element => {
  if (!leadId || !reference) return <span className="text-sm text-zinc-400">No lead</span>;
  return (
    <div className="flex flex-col">
      <Link to={`/leads/${leadId}`} className="text-sm text-emerald-700 hover:underline">
        {reference}
      </Link>
      <span className="text-[11px] text-zinc-500">{agentName ?? 'Unassigned'}</span>
    </div>
  );
};

export const WhatsAppLogPage = (): React.JSX.Element => {
  const [tab, setTab] = useState<Tab>('messages');
  const gridState = useDataGridState({ filterIds: ['scope'] });
  const { state } = gridState;
  const [range, setRange] = useState<UzDateRange>({ from: null, to: null });

  const query = {
    page: state.page,
    pageSize: state.pageSize,
    sortDir: state.sortDir,
    onlyProblems: state.filters.scope !== 'all',
    search: state.q.trim() || undefined,
    from: isoDay(range.from),
    to: isoDay(range.to),
  };

  const messages = useWhatsAppLogMessages(query, tab === 'messages');
  const calls = useWhatsAppLogCalls(query, tab === 'calls');
  const active = tab === 'messages' ? messages : calls;

  const pagination = active.data
    ? {
        page: active.data.pagination.page,
        pageSize: active.data.pagination.pageSize,
        totalItems: active.data.pagination.totalItems,
      }
    : null;

  const messageColumns = useMemo(
    (): ReadonlyArray<DataGridColumn<WhatsAppLogMessageItem>> => [
      {
        id: 'at',
        header: 'When',
        width: '150px',
        cell: (row) => (
          <span className="text-sm text-zinc-700 tabular-nums">{formatWhen(row.at)}</span>
        ),
      },
      {
        id: 'customer',
        header: 'Customer',
        cell: (row) => <Customer name={row.contactName} phone={row.phone} />,
      },
      {
        id: 'lead',
        header: 'Lead / owner',
        cell: (row) => (
          <LeadCell
            leadId={row.leadId}
            reference={row.leadReference}
            agentName={row.leadAgentName}
          />
        ),
      },
      {
        id: 'status',
        header: 'Status',
        width: '110px',
        cell: (row) => <StatusPill status={row.status} />,
      },
      {
        id: 'what',
        header: 'Message',
        cell: (row) => (
          <div className="flex flex-col">
            <span className="text-[11px] text-zinc-500">
              {row.templateName ? `Template · ${row.templateName}` : row.kind}
            </span>
            <span className="line-clamp-2 text-sm text-zinc-700">{row.preview ?? '-'}</span>
          </div>
        ),
      },
      {
        id: 'why',
        header: 'Why it failed',
        cell: (row) => <span className="text-sm text-rose-700">{row.errorMessage ?? '-'}</span>,
      },
      {
        id: 'sentBy',
        header: 'Sent by',
        width: '150px',
        cell: (row) => <span className="text-sm text-zinc-600">{row.sentByName ?? 'System'}</span>,
      },
    ],
    [],
  );

  const callColumns = useMemo(
    (): ReadonlyArray<DataGridColumn<WhatsAppLogCallItem>> => [
      {
        id: 'at',
        header: 'When',
        width: '150px',
        cell: (row) => (
          <span className="text-sm text-zinc-700 tabular-nums">{formatWhen(row.at)}</span>
        ),
      },
      {
        id: 'customer',
        header: 'Customer',
        cell: (row) => <Customer name={row.contactName} phone={row.phone} />,
      },
      {
        id: 'lead',
        header: 'Lead / owner',
        cell: (row) => (
          <LeadCell
            leadId={row.leadId}
            reference={row.leadReference}
            agentName={row.leadAgentName}
          />
        ),
      },
      {
        id: 'direction',
        header: 'Direction',
        width: '110px',
        cell: (row) => (
          <span className="text-sm text-zinc-600">
            {row.direction === 'OUTGOING' ? 'Outgoing' : 'Incoming'}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        width: '110px',
        cell: (row) => <StatusPill status={row.status} />,
      },
      {
        id: 'duration',
        header: 'Duration',
        width: '100px',
        cell: (row) => (
          <span className="text-sm text-zinc-600 tabular-nums">
            {row.durationSeconds
              ? `${Math.floor(row.durationSeconds / 60)}:${String(row.durationSeconds % 60).padStart(2, '0')}`
              : '-'}
          </span>
        ),
      },
      {
        id: 'why',
        header: 'Why it failed',
        cell: (row) => <span className="text-sm text-rose-700">{row.failedReason ?? '-'}</span>,
      },
      {
        id: 'handledBy',
        header: 'Handled by',
        width: '150px',
        cell: (row) => <span className="text-sm text-zinc-600">{row.handledByName ?? '-'}</span>,
      },
    ],
    [],
  );

  return (
    <PageShell
      title="WhatsApp log"
      description="Every WhatsApp message and call that did not get through, with the customer, the lead and its owner. Agents see none of this in the chat widget - it is here so someone can act on it. Admin-only."
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-zinc-200 bg-white p-0.5">
          {(['messages', 'calls'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                tab === t ? 'bg-emerald-600 text-white' : 'text-zinc-600 hover:bg-zinc-50'
              }`}
            >
              {t === 'messages' ? 'Messages' : 'Calls'}
            </button>
          ))}
        </div>
        <UzDateRangePicker
          value={range}
          onValueChange={setRange}
          placeholder="Filter by date range"
          triggerClassName="w-64"
        />
      </div>

      {tab === 'messages' ? (
        <DataGrid<WhatsAppLogMessageItem>
          gridId="whatsapp-log-messages"
          data={messages.data?.data ?? []}
          columns={messageColumns}
          filters={SCOPE_FILTER}
          getRowId={(row) => row.id}
          isLoading={messages.isLoading}
          isError={messages.isError}
          onRetry={() => void messages.refetch()}
          pagination={pagination}
          state={state}
          onStateChange={gridState}
          searchPlaceholder="Search number, name, template or error"
          emptyTitle="Nothing failed"
          emptyDescription="Messages that WhatsApp refused show up here, with the reason."
          tableLayout="auto"
        />
      ) : (
        <DataGrid<WhatsAppLogCallItem>
          gridId="whatsapp-log-calls"
          data={calls.data?.data ?? []}
          columns={callColumns}
          filters={SCOPE_FILTER}
          getRowId={(row) => row.id}
          isLoading={calls.isLoading}
          isError={calls.isError}
          onRetry={() => void calls.refetch()}
          pagination={pagination}
          state={state}
          onStateChange={gridState}
          searchPlaceholder="Search number, name or reason"
          emptyTitle="No missed or failed calls"
          emptyDescription="Calls that did not connect show up here."
          tableLayout="auto"
        />
      )}
    </PageShell>
  );
};
